import React, { useEffect, useState, useCallback } from "react";
import ReactModalWrap from "../../components/UI/Modals/ReactModalWrap/ReactModalWrap";
import Button from "../../components/UI/Buttons/Button/Button";
import styles from "./RFZOReminderModal.module.scss";
import axios, { baseUrl } from "../../axios/axios";
import TextField from "../../components/TextField/TextField";
import SearchBar from "../../components/UI/Modals/RFZOTherapyRequestModal/SearchWrapper/SearchBar/SearchBar";
import MedicineSearchBar from "../../components/MedicineSearchBar/MedicineSearchBar";
import RFZOTherapyReminderGroupModal, {
  fetchAllGroups,
} from "./RFZOTherapyReminderGroupModal";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import RFZOArticleModal from "../../components/UI/Modals/ReminderModal/RFZOArticleModal/RFZOArticleModal";
import ArticleItem from "../../components/UI/Modals/ReminderModal/ArticleItem/ArticleItem";
import moment from "moment";
import { useMarketplaceInfo } from "../../hooks/useMarketplaceInfo";
import { calculateCentralTherapyDate } from "../../utils/rfzo/dateUtils";
import { PatientReminderData } from "../../types/reminderTypes";

type Props = {
  customButton?: any;
  onSubmit?: (message: string, updatedPatient?: any) => void;
  initialData?: PatientReminderData | null;
  duplicateButton?: {
    class?: string;
    buttonType?: string;
    text?: string;
    disabled?: boolean;
    icon?: React.ReactNode;
    title?: string;
  };
};

const THERAPY_REQUEST_INIT = {
  userLbo: "",
  userFirstName: "",
  userSecondName: "",
  userEmail: "",
  userId: null,
};

const MODAL_DATA_INIT = {
  willBeReady: null,
  lastTherapyDate: null,
  note: "",
  userPhone: "",
};

const SELECTED_GROUPS_INIT: any[] = [];
const SELECTED_ARTICLES_INIT: any[] = [];

const Row = ({ children }: any) => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        margin: "20px 0",
      }}
    >
      {children}
    </div>
  );
};

const fetchFavoriteUsers = (
  marketplaceId: string | number,
  branchMarketplaceId: string | number,
) => {
  return axios.get(
    baseUrl +
      `/marketplace/favourite_users/${marketplaceId}/${branchMarketplaceId}`,
    {},
  );
};

const createNewReminder = (
  data: any,
  itemList: any,
  therapyRequest: any,
  rfzoTherapyReminderGroupsIds: any,
  branchMarketplaceId: string | number,
  marketplaceId: string | number,
) => {
  if (!branchMarketplaceId || !marketplaceId) {
    console.warn("Cannot create reminder: marketplace info not available");
    return Promise.reject(new Error("Missing marketplace IDs"));
  }

  const willBeReady = data.willBeReady
    ? moment(data.willBeReady).format("YYYY-MM-DD")
    : null;
  const lastTherapyDate = data.lastTherapyDate
    ? moment(data.lastTherapyDate).format("YYYY-MM-DD")
    : null;

  // Ensure items have proper structure
  const sanitizedItems = itemList.map((item: any) => ({
    itemId: item.itemResponse?.id || item.itemId || item.id,
    articleName: item.itemResponse?.name || item.articleName || item.name,
    rfzoQuantity: item.rfzoQuantity || 1,
    rfzoParticipationPrice: item.rfzoParticipationPrice || 0,
  }));

  return axios.post(
    baseUrl + `/rfzo_therapy_reminder`,
    {
      ...data,
      willBeReady: willBeReady,
      lastTherapyDate: lastTherapyDate,
      branchMarketplaceId: branchMarketplaceId,
      marketplaceId: marketplaceId,
      therapyRequest: therapyRequest,
      items: sanitizedItems,
      rfzoTherapyReminderGroupsIds:
        rfzoTherapyReminderGroupsIds?.map((g: any) => g?.value) || [],
    },
    {
      headers: {
        "ApotekaRS-Branch-Marketplace-ID": branchMarketplaceId.toString(),
      },
    },
  );
};

const editReminder = (
  data: any,
  itemList: any,
  therapyRequest: any,
  rfzoTherapyReminderGroupsIds: any,
  reminderId: number | string,
  branchMarketplaceId: string | number,
  marketplaceId: string | number,
) => {
  if (!branchMarketplaceId || !marketplaceId) {
    console.warn("Cannot edit reminder: marketplace info not available");
    return Promise.reject(new Error("Missing marketplace IDs"));
  }

  // Proper item mapping with null safety
  const itemsRemap = itemList.map((item: any) => ({
    itemId: item.itemResponse?.id || item.itemId || item.id,
    articleName: item.itemResponse?.name || item.articleName || item.name,
    rfzoQuantity: item.rfzoQuantity || 1,
    rfzoParticipationPrice: item.rfzoParticipationPrice || 0,
  }));

  // Clean therapy request data
  const therapyRequestPayload = {
    userLbo: therapyRequest.userLbo,
    userFirstName: therapyRequest.userFirstName,
    userSecondName: therapyRequest.userSecondName,
    userEmail: therapyRequest.userEmail,
  };

  const requestPayload = {
    note: data.note || "",
    userPhone: data.userPhone || "",
    branchMarketplaceId: branchMarketplaceId,
    marketplaceId: marketplaceId,
    therapyRequest: therapyRequestPayload,
    items: itemsRemap,
    rfzoTherapyReminderGroupsIds:
      rfzoTherapyReminderGroupsIds?.map((g: any) => g?.value) || [],
    // REMOVED: "id" field completely - this was causing constraint violation
  };

  return axios
    .put(baseUrl + `/rfzo_therapy_reminder`, requestPayload, {
      headers: {
        "ApotekaRS-Branch-Marketplace-ID": branchMarketplaceId.toString(),
      },
    })
    .then((response) => {
      console.log("Edit reminder success:", response.data);
      return response;
    })
    .catch((error) => {
      console.error(
        "Edit reminder failed:",
        error.response?.data || error.message,
      );
      throw error;
    });
};

export default function RFZOReminderModal({
  customButton,
  onSubmit,
  initialData,
  duplicateButton,
}: Props) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currentArticle, setCurrentArticle] = useState<any>(null);

  const userId = useSelector((state: RootState) => state.auth.userInfo?.id);
  const { branchMarketplaceId, marketplaceId, isReady } = useMarketplaceInfo();

  const [allGroups, setAllGroups] = useState<any[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState<boolean>(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);

  const [modalData, setModalData] = useState<any>(MODAL_DATA_INIT);

  const [articleList, setArticlesList] = useState<any[]>(
    SELECTED_ARTICLES_INIT,
  );
  const [therapyRequest, setTherapyRequest] =
    useState<any>(THERAPY_REQUEST_INIT);

  const [selectedGroup, setSelectedGroup] = useState<any[] | null>(
    SELECTED_GROUPS_INIT,
  );

  const [favoriteUsers, setFavoriteUsers] = useState<any[]>([]);

  const refreshGroups = useCallback(async () => {
    // Guard against missing marketplace info
    if (!isReady) {
      console.warn("Cannot refresh groups: marketplace info not ready");
      return;
    }

    setIsLoadingGroups(true);
    try {
      const groups = await fetchAllGroups(branchMarketplaceId, marketplaceId);
      setAllGroups(groups.data);
    } catch (error) {
      console.error("Failed to refresh groups:", error);
    } finally {
      setIsLoadingGroups(false);
    }
  }, [isReady, branchMarketplaceId, marketplaceId]);

  useEffect(() => {
    if (isOpen && isReady) {
      // Fetch groups
      refreshGroups();

      // Fetch favorite users with loading state
      setIsLoadingUsers(true);
      fetchFavoriteUsers(marketplaceId, branchMarketplaceId)
        .then((res) => setFavoriteUsers(res.data))
        .catch((error) => {
          console.error("Failed to fetch favorite users:", error);
          setFavoriteUsers([]); // Graceful fallback
        })
        .finally(() => setIsLoadingUsers(false));
    } else if (!isOpen) {
      // Reset state when modal closes
      setTherapyRequest(THERAPY_REQUEST_INIT);
      setArticlesList(SELECTED_ARTICLES_INIT);
      setModalData(MODAL_DATA_INIT);
      setSelectedGroup(SELECTED_GROUPS_INIT);
    }
  }, [
    isOpen,
    refreshGroups,
    isReady,
    marketplaceId,
    branchMarketplaceId,
  ]);

  const onSubmitArticle = (article: any) => {
    setArticlesList((prev) => [...prev, article]);
    setCurrentArticle(null);
  };

  const customClickHandler = (val: any) => {
    setCurrentArticle(val);
  };

  const removeItemFromArticleList = (article: any) => {
    setArticlesList((prev) => {
      const temp = prev.filter((i) => i.itemId !== article.itemId);
      return temp;
    });
  };

  const validatorFunction = (therapyRequest: any) => {
    if (!therapyRequest.userLbo || therapyRequest.userLbo.length !== 11) {
      return true;
    }
    return false;
  };

  const onUserSearchClick = (data: any) => {
    setTherapyRequest({
      userLbo: data?.lbo,
      userFirstName: data?.firstName,
      userSecondName: data?.secondName,
      userEmail: data?.username,
      userId: data?.id,
    });
    setModalData({
      ...modalData,
      userPhone: data?.phoneNumber,
    });
  };

  const [showNewUserFields, setNewUserFields] = useState<boolean>(false);

  const previewMode = customButton?.mode === "preview";
  const isEditMode = !!customButton;
  const isDuplicateMode = !customButton && !!initialData;

  useEffect(() => {
    if (isOpen && customButton) {
      const modalDataPassed = customButton?.data;
      setTherapyRequest({
        userLbo: modalDataPassed?.userLBO,
        userFirstName: modalDataPassed?.userFirstName,
        userSecondName: modalDataPassed?.userSecondName,
        userEmail: modalDataPassed?.email,
        userId: modalDataPassed?.userResponse?.id || 1,
      });

      const willBeReady = moment(customButton?.data?.willBeReadyFrom).toDate();
      const lastTherapyDate = moment(
        customButton?.data?.willBeReadyTo,
      ).toDate();
      setModalData({
        willBeReady: willBeReady,
        lastTherapyDate: lastTherapyDate,
        note: modalDataPassed?.note,
        userPhone: modalDataPassed?.userPhone,
      });

      const articles = customButton?.data?.items?.map((it: any) => ({
        ...it,
        itemId: it?.itemResponse?.id,
        ...it?.itemResponse,
        articleName: it?.itemResponse?.name,
        // Explicitly preserve custom values that should not be overwritten
        rfzoParticipationPrice: it.rfzoParticipationPrice, // Use custom price
        rfzoQuantity: it.rfzoQuantity, // Preserve custom quantity
      }));
      setArticlesList(articles || []);

      // Load the patient's group memberships from the groups array
      if (modalDataPassed?.groups?.length > 0) {
        const selectedGroups = modalDataPassed.groups.map((group: any) => ({
          value: group.id,
          label: group.name,
        }));
        setSelectedGroup(selectedGroups);
      }
    }
    setNewUserFields(false);
  }, [customButton, isOpen]);

  // Handle duplicate mode - pre-populate from initialData
  useEffect(() => {
    if (isOpen && initialData && !customButton) {
      // Populate patient info
      setTherapyRequest({
        userLbo: initialData.userLBO || initialData.lbo,
        userFirstName: initialData.userFirstName,
        userSecondName: initialData.userSecondName,
        userEmail: initialData.userEmail || initialData.email,
        userId: (initialData as any).userResponse?.id || null,
      });

      // Populate phone and note (but NOT dates - user must choose new dates)
      setModalData({
        willBeReady: null,
        lastTherapyDate: null,
        note: initialData.note === "/" ? "" : initialData.note,
        userPhone: initialData.userPhone || initialData.phone,
      });

      // Populate articles with preserved prices and quantities
      const articles = initialData.items?.map((it: any) => ({
        ...it,
        itemId: it?.itemResponse?.id || it.itemId,
        ...it?.itemResponse,
        articleName: it?.itemResponse?.name || it.articleName,
        rfzoParticipationPrice: it.rfzoParticipationPrice,
        rfzoQuantity: it.rfzoQuantity,
      }));
      setArticlesList(articles || []);

      // Populate groups
      if (initialData.groups?.length > 0) {
        const selectedGroups = initialData.groups.map((group: any) => ({
          value: group.id,
          label: group.name,
        }));
        setSelectedGroup(selectedGroups);
      }

      // Show user fields since we have user data
      setNewUserFields(true);
    }
  }, [initialData, customButton, isOpen]);

  const handleSave = useCallback(async () => {
    console.log("🟡 RFZOReminderModal: handleSave called", {
      isReady,
      customButton: !!customButton,
      onSubmit: typeof onSubmit,
    });

    if (!isReady) {
      console.warn("Cannot save reminder: marketplace info not ready");
      alert("Molimo sačekajte da se učitaju informacije o apoteci.");
      return;
    }

    if (!userId) {
      alert("Došlo je do greške. Molimo osvežite stranicu i pokušajte ponovo.");
      return;
    }

    const requestTherapy = { ...therapyRequest, userAuthorizedId: userId };

    try {
      if (customButton) {
        console.log("🟡 RFZOReminderModal: Edit mode - calling editReminder");
        // Edit mode
        await editReminder(
          modalData,
          articleList,
          requestTherapy,
          selectedGroup,
          customButton.data?.id,
          branchMarketplaceId,
          marketplaceId,
        );

        console.log("🟢 RFZOReminderModal: editReminder succeeded");
        setIsOpen(false);
        console.log(
          "🟡 RFZOReminderModal: Modal closed, about to call onSubmit",
        );

        if (onSubmit) {
          console.log(
            "🟢 RFZOReminderModal: Calling onSubmit for edit with updated patient data",
          );

          // Construct updated patient data from modal state
          const updatedPatient = {
            ...customButton.data, // Start with original data
            // Update with modal changes
            note: modalData.note,
            userPhone: modalData.userPhone,
            userFirstName: therapyRequest.userFirstName,
            userSecondName: therapyRequest.userSecondName,
            userEmail: therapyRequest.userEmail,
            items: articleList,
            groups:
              selectedGroup?.map((g: any) => ({
                id: g.value,
                name: g.label,
              })) || [],
            modified: new Date().toISOString(), // Mark as recently modified
          };

          onSubmit("Uspešno ažuriran podsetnik", updatedPatient);
          console.log(
            "🟢 RFZOReminderModal: onSubmit called successfully for edit with updated data",
          );
        } else {
          console.error(
            "❌ RFZOReminderModal: onSubmit is undefined for edit!",
          );
        }
      } else {
        console.log(
          "🟡 RFZOReminderModal: Create mode - calling createNewReminder",
        );
        // Create mode
        await createNewReminder(
          modalData,
          articleList,
          requestTherapy,
          selectedGroup,
          branchMarketplaceId,
          marketplaceId,
        );

        console.log("🟢 RFZOReminderModal: createNewReminder succeeded");
        setIsOpen(false);
        console.log(
          "🟡 RFZOReminderModal: Modal closed, about to call onSubmit",
        );

        if (onSubmit) {
          console.log("🟢 RFZOReminderModal: Calling onSubmit for create");
          onSubmit("Uspešno kreiran podsetnik");
          console.log(
            "🟢 RFZOReminderModal: onSubmit called successfully for create",
          );
        } else {
          console.error(
            "❌ RFZOReminderModal: onSubmit is undefined for create!",
          );
        }
      }
    } catch (error) {
      console.error("❌ RFZOReminderModal: API call failed:", error);
      alert(
        "Došlo je do greške prilikom čuvanja podsetnika. Molimo pokušajte ponovo.",
      );
    }
  }, [
    isReady,
    userId,
    customButton,
    modalData,
    articleList,
    therapyRequest,
    selectedGroup,
    branchMarketplaceId,
    marketplaceId,
    onSubmit,
  ]);

  // Determine which button config to use
  const buttonConfig = customButton || duplicateButton;

  // Show loading while marketplace info loads
  if (isOpen && !isReady) {
    return (
      <div style={{ position: "relative" }}>
        {!buttonConfig && (
          <Button
            className={styles.modalButton}
            onClick={() => setIsOpen(true)}
            buttonType="blue"
          >
            Dodaj novog pacijenta
          </Button>
        )}
        {buttonConfig && (
          <Button
            className={buttonConfig?.class}
            disabled={buttonConfig?.disabled}
            onClick={() => setIsOpen(true)}
            buttonType={buttonConfig?.buttonType}
            title={buttonConfig?.text}
            aria-label={buttonConfig?.text}
          >
            {buttonConfig?.icon || buttonConfig?.text}
          </Button>
        )}
        {isOpen && (
          <ReactModalWrap
            customStyle={{ content: { width: "550px" } }}
            isOpen={isOpen}
            close={() => setIsOpen(false)}
          >
            <div className={styles.contentWrap}>
              <div className={styles.header}>
                Učitavanje informacija o apoteci...
              </div>
              <div style={{ padding: "40px", textAlign: "center" }}>
                <div>Molimo sačekajte...</div>
              </div>
            </div>
          </ReactModalWrap>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      {currentArticle && (
        <RFZOArticleModal
          onSubmitArticle={onSubmitArticle}
          show={!!currentArticle}
          selectedItem={currentArticle}
        />
      )}
      {!buttonConfig && (
        <Button
          className={styles.modalButton}
          onClick={() => setIsOpen(true)}
          buttonType="blue"
        >
          Dodaj novog pacijenta
        </Button>
      )}
      {buttonConfig && (
        <Button
          className={buttonConfig?.class}
          disabled={buttonConfig?.disabled}
          onClick={() => setIsOpen(true)}
          buttonType={buttonConfig?.buttonType}
          title={buttonConfig?.text}
          aria-label={buttonConfig?.text}
        >
          {buttonConfig?.icon || buttonConfig?.text}
        </Button>
      )}
      {isOpen && (
        <ReactModalWrap
          customStyle={{ content: { width: "550px" } }}
          isOpen={isOpen}
          close={() => setIsOpen(false)}
        >
          <div className={styles.contentWrap}>
            <div className={styles.header}>
              {isDuplicateMode
                ? "Ponovi podsetnik"
                : "Dodaj podsetnik za novog pacijenta"}
            </div>
            <hr />
            <div className={styles.smallHeader}>Opšte informacije</div>

            <div className={styles.userSection}>
              {!(therapyRequest?.userId || showNewUserFields) && (
                <>
                  <SearchBar
                    className={styles.searchBar}
                    label={
                      isLoadingUsers
                        ? "Učitavanje korisnika..."
                        : "Pretražite korisnike"
                    }
                    customClickHandler={(user: any) => onUserSearchClick(user)}
                    isUsers
                    data={favoriteUsers}
                  />

                  <Button
                    onClick={() => setNewUserFields(true)}
                    className={styles.addNewPatientButton}
                    disabled={isLoadingUsers}
                  >
                    Novi pacijent
                  </Button>
                </>
              )}
              {(therapyRequest?.userId || showNewUserFields) && (
                <>
                  <Row>
                    <TextField
                      onChange={(val: any) =>
                        setTherapyRequest({
                          ...therapyRequest,
                          userFirstName: val,
                        })
                      }
                      wrapClass={styles.textFields}
                      label="Ime"
                      value={therapyRequest?.userFirstName}
                    />
                    <TextField
                      onChange={(val: any) =>
                        setTherapyRequest({
                          ...therapyRequest,
                          userSecondName: val,
                        })
                      }
                      wrapClass={styles.textFields}
                      label="Prezime"
                      value={therapyRequest?.userSecondName}
                    />
                  </Row>

                  <Row>
                    <TextField
                      disabled={previewMode}
                      wrapClass={styles.textFields}
                      onChange={(val: any) =>
                        setTherapyRequest({ ...therapyRequest, userLbo: val })
                      }
                      value={therapyRequest?.userLbo}
                      label="LBO"
                      validator={(val: any) => val?.length !== 11}
                    />

                    <TextField
                      disabled={previewMode}
                      onChange={(val: any) =>
                        setTherapyRequest({ ...therapyRequest, userEmail: val })
                      }
                      wrapClass={styles.textFields}
                      value={therapyRequest?.userEmail}
                      label="Email"
                    />
                  </Row>

                  <Row>
                    <TextField
                      onChange={(val: any) =>
                        setModalData({ ...modalData, userPhone: val })
                      }
                      wrapClass={styles.textFields}
                      value={modalData?.userPhone}
                      label="Broj telefona"
                    />
                  </Row>
                </>
              )}
              <hr />
            </div>

            <div className={styles.smallHeader}>Podsetnik</div>

            <MedicineSearchBar
              className={styles.searchBar}
              onSelect={customClickHandler}
              label="Proizvodi"
            />
            {articleList.map((article: any, key: number) => (
              <ArticleItem
                xIcon
                removeArticle={removeItemFromArticleList}
                article={article}
                key={key}
              />
            ))}

            {/* Date Section - Create vs Edit Mode */}
            {isEditMode && customButton?.data ? (
              // EDIT MODE: Show 4 read-only dates that look like DatePickers
              <>
                <Row>
                  <div className={styles.dateWrap}>
                    <label>Datum dospeća</label>
                    <div className={styles.datePickerWrapper}>
                      <div className={styles.DatePickerButtonStyle}>
                        {moment(customButton.data.willBeReadyFrom).format(
                          "DD.MM.YYYY",
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={styles.dateWrap}>
                    <label>Centralni datum propisivanja</label>
                    <div className={styles.datePickerWrapper}>
                      <div className={styles.DatePickerButtonStyle}>
                        {moment(
                          calculateCentralTherapyDate(
                            customButton.data.willBeReadyFrom,
                          ),
                        ).format("DD.MM.YYYY")}
                      </div>
                    </div>
                  </div>
                </Row>
                <Row>
                  <div className={styles.dateWrap}>
                    <label>Poslednji dan terapije</label>
                    <div className={styles.datePickerWrapper}>
                      <div className={styles.DatePickerButtonStyle}>
                        {moment(customButton.data.willBeReadyTo).format(
                          "DD.MM.YYYY",
                        )}
                      </div>
                    </div>
                  </div>
                </Row>
              </>
            ) : (
              // CREATE MODE: Show 2 DatePickers
              <Row>
                <div className={styles.dateWrap}>
                  <label>Datum propisivanja</label>
                  <DatePicker
                    isClearable
                    popperPlacement="bottom"
                    dateFormat="dd.MM.yyyy"
                    placeholderText="dd.MM.yyyy"
                    wrapperClassName={styles.datePickerWrapper}
                    selected={modalData?.willBeReady}
                    className={styles.DatePickerButtonStyle}
                    onChange={(val: any) =>
                      setModalData({ ...modalData, willBeReady: val })
                    }
                  />
                </div>
                <div className={styles.dateWrap}>
                  <label>Poslednja terapija</label>
                  <DatePicker
                    isClearable
                    popperPlacement="bottom"
                    dateFormat="yyyy-MM"
                    placeholderText="Poslednji mesec dospeća terapije"
                    wrapperClassName={styles.datePickerWrapper}
                    selected={modalData?.lastTherapyDate}
                    className={styles.DatePickerButtonStyle}
                    showMonthYearPicker
                    onChange={(val: any) =>
                      setModalData({ ...modalData, lastTherapyDate: val })
                    }
                  />
                </div>
              </Row>
            )}
            <hr />

            <div className={styles.listSection}>
              <div className={styles.header}>Lista</div>
              <div className={styles.selectListWrap}>
                <Select
                  closeMenuOnSelect={false}
                  classNamePrefix="itam-select"
                  menuPlacement="bottom"
                  className={styles.selectList}
                  placeholder={
                    isLoadingGroups ? "Učitavanje lista..." : "Izaberi listu"
                  }
                  isMulti
                  isLoading={isLoadingGroups}
                  value={selectedGroup}
                  onChange={(val: any) => setSelectedGroup(val)}
                  options={allGroups?.map((g: any) => ({
                    value: g?.id,
                    label: g?.name,
                  }))}
                />
                <RFZOTherapyReminderGroupModal
                  customButton={{
                    class: styles.openButton,
                    buttonType: "white",
                    text: "+ Dodaj novu listu",
                    mode: "preview",
                  }}
                  onGroupCreated={refreshGroups}
                />
              </div>
            </div>

            <hr />

            <div className={styles.smallHeader}>Napomena</div>

            <textarea
              onChange={(e: any) =>
                setModalData({ ...modalData, note: e?.target?.value })
              }
              value={modalData?.note}
              className={styles.note}
              placeholder="Dodaj napomenu"
            />

            <hr />

            <div className={styles.smallHeader}>Status</div>
            <div
              className={`${styles.statusDisplay} ${
                styles[customButton?.data?.status?.toLowerCase() || "pending"]
              }`}
            >
              {customButton?.data?.status
                ? mapStatuses(customButton.data.status)
                : "Za slanje"}
            </div>
            <hr />

            <Button
              disabled={validatorFunction(therapyRequest) || !isReady}
              buttonType="blue"
              onClick={handleSave}
            >
              Sačuvaj
            </Button>
          </div>
        </ReactModalWrap>
      )}
    </div>
  );
}

const STATUSES: { [key: string]: string } = {
  DELETED: "Obrisan",
  SUSPENDED: "Propušten",
  EXPIRED: "Istekao",
  PENDING: "Za slanje",
  WAITING: "Na čekanju za registraciju",
  ORDERED: "Korisnik je rezervisao terapiju nakon podsetnika",
};

const mapStatuses = (status: string): string => {
  return STATUSES[status] ?? status;
};
